import { api } from '@/lib/api/client';

export interface HomeStats {
  courses: number;
  learners: number;
  certificates: number;
  avgRating: number | null;
}

export function getHomeStats(): Promise<HomeStats> {
  return api.get<HomeStats>('/stats/home').then((r) => r.data);
}

export interface EventLite {
  slug: string;
  title: string;
}

export function getEvents(): Promise<{ upcoming: EventLite[]; recorded: EventLite[] }> {
  return api.get<{ upcoming: EventLite[]; recorded: EventLite[] }>('/events').then((r) => r.data);
}

export interface EventData {
  slug: string;
  title: string;
  category: string;
  type: string;
  startsAt: string | null;
  heldAt: string | null;
  signups: number;
  isRecorded: boolean;
}

export function getEvent(slug: string): Promise<{ event: EventData }> {
  return api.get<{ event: EventData }>(`/events/${slug}`).then((r) => r.data);
}

export function signupEvent(slug: string): Promise<unknown> {
  return api.post(`/events/${slug}/signup`).then((r) => r.data);
}

export interface Bundle {
  slug: string;
  title: string;
  blurb: string;
  icon: string | null;
}

export function getBundles(): Promise<{ bundles: Bundle[] }> {
  return api.get<{ bundles: Bundle[] }>('/bundles').then((r) => r.data);
}

export interface Promo {
  id: string;
  tag: string;
  title: string;
  sub: string;
  cta: string;
  action: string;
}

export function getPromos(): Promise<{ promos: Promo[] }> {
  return api.get<{ promos: Promo[] }>('/promos').then((r) => r.data);
}

export interface Centre {
  id: string;
  slug: string;
  name: string;
  address: string;
  phone: string;
  mapUrl: string;
  rooms: number;
}

export function getCentres(): Promise<{ centres: Centre[] }> {
  return api.get<{ centres: Centre[] }>('/centres').then((r) => r.data);
}

export interface ArticleSummary {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  author: string;
  publishedAt: string;
  readMins: number;
}

export interface Article extends ArticleSummary {
  body: string[];
}

export function getArticles(cat: string | null): Promise<{ articles: ArticleSummary[]; categories: string[] }> {
  const qs = cat ? `?category=${encodeURIComponent(cat)}` : '';
  return api.get<{ articles: ArticleSummary[]; categories: string[] }>(`/articles${qs}`).then((r) => r.data);
}

export function getArticle(slug: string): Promise<{ article: Article; related: ArticleSummary[] }> {
  return api.get<{ article: Article; related: ArticleSummary[] }>(`/articles/${slug}`).then((r) => r.data);
}

export interface Resource {
  slug: string;
  title: string;
  category: string;
  type: string;
  size: string;
  downloads: number;
  gated: boolean;
}

export function getResources(): Promise<{ resources: Resource[]; categories: string[] }> {
  return api.get<{ resources: Resource[]; categories: string[] }>('/resources').then((r) => r.data);
}

export function downloadResource(slug: string): Promise<unknown> {
  return api.post(`/resources/${slug}/download`).then((r) => r.data);
}

export interface VerifyResult {
  valid: boolean;
  certId?: string;
  holder?: string;
  course?: string;
  hours?: number;
  instructor?: string;
  issuedAt?: string;
}

export function verifyCertificate(code: string): Promise<VerifyResult> {
  return api.get<VerifyResult>(`/certificates/verify/${encodeURIComponent(code)}`).then((r) => r.data);
}

export interface Notif {
  id: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

export function getNotifications(): Promise<{ notifications: Notif[]; unread: number }> {
  return api.get<{ notifications: Notif[]; unread: number }>('/notifications').then((r) => r.data);
}

export function markNotificationsRead(): Promise<unknown> {
  return api.post('/notifications/read').then((r) => r.data);
}

export interface SiteWideBanner {
  id: string;
  text: string;
}

/** Active site-wide promo banner for the public header strip. */
export async function getSiteBanner(): Promise<SiteWideBanner | null> {
  // Admin demo mode owns the banner in localStorage — read it directly.
  const { demoGetPublicBanner } = await import('@/pages/admin/demo/store/adminDemoStore');
  return demoGetPublicBanner();
}
